import { orderByStart } from './dayShape'
import { BRAND } from '../config/brand'

export { dayPhase, isDayClosed } from './todayPhase'
export type { DayPhase } from './todayPhase'
/**
 * One Clear Thing — pure logic for the redesigned /today page
 * ===========================================================
 *
 * Everything here is pure and clock-free (the caller passes hours/minutes), so
 * the candidate engine, phase register, free-time math, and the core-complete
 * derivation are all unit-testable without React.
 *
 * Doctrine: capacity framing, never worth framing. Every user-facing literal
 * in the page lives in ALL_STATIC_STRINGS below and is guarded by
 * validateEnergyModeCopy in a unit test (and a dev-mode console.assert), so a
 * worth-language regression ("behind", "quota", "%", …) fails loudly.
 *
 * R9 invariant: NOTHING in this module accepts a mood value. Mood is
 * render-only on the page — there is structurally no code path from the mood
 * slider to limits, slicing, candidate choice, or copy.
 */


/** Parse "HH:MM-HH:MM" / "HH:MM" style slots to the start hour. */
export function parseSlotHour(slot?: string): number | null {
  if (!slot) return null
  const m = slot.match(/^(\d{1,2}):/)
  return m ? parseInt(m[1], 10) : null
}

/**
 * Parse a time-of-day to minutes-from-midnight. Accepts BOTH bare "HH:MM..."
 * strings and ISO datetimes ("2026-06-10T09:00:00") — production
 * scheduled_events carry ISO timestamps (the legacy view split on 'T').
 */
export function parseHHMM(value?: string): number | null {
  if (!value) return null
  const m = value.match(/(?:^|T)(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

/** Normalize a start/end time for display: "HH:MM" from either a bare time or
 * an ISO datetime; falls back to the raw string when nothing parses. */
export function displayTime(value?: string): string {
  if (!value) return ''
  const m = value.match(/(?:^|T)(\d{1,2}):(\d{2})/)
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : value
}

// ── Candidate engine ────────────────────────────────────────────────────────

/** The minimal task shape the engine needs (structural subset of FocusTaskItem). */
export interface CandidateTask {
  task_id: string
  status: string
  estimated_duration?: number
  scheduled_time_slot?: string
  user_pinned_for_today?: boolean
  deadline_type?: 'soft' | 'hard'
  reasoning_tags: string[]
}

export type WhyChip =
  | 'Prepared for today'
  | 'Already started'
  | 'Pinned by you'
  | 'Due soon'
  | 'Scheduled about now'
  | 'A quick one'
  | 'Next in line'
  /** Set by the page, never by the selector: the clock promoted this one
   *  over the selector's pick (`executionWalk`, North Star rule 5). */
  | 'Next on your schedule'

const WHY_BY_RULE: WhyChip[] = [
  'Prepared for today',
  'Already started', 'Pinned by you', 'Due soon', 'Scheduled about now', 'A quick one', 'Next in line',
]

/**
 * Order the candidate pool for the One Clear Thing card. Rules, in priority
 * order: PREPARED PICK → in-progress → user-pinned → due-soon/hard → scheduled
 * ±2h of now → quick-win tag → ascending duration. Stable dedupe keeps the
 * FIRST rule a task matched, so the why-chip is honest about why it leads.
 *
 * `preparedTaskId` is rule 0 (2026-09-08). Auto-Mom's prepared day used to be
 * a SECOND opinion rendered beside this card: the curtain offered "hasn't had
 * attention lately · Adopt" for the very task this card was calling "Next in
 * line · Start this". Two engines answering one question on one screen, which
 * doctrine does not allow — `TodaysReleaseCard` had carried a note asking for
 * exactly this wiring since 2026-08-30. Now the prepared day DECIDES what
 * leads, and the chip says so, instead of arguing with it one zone below.
 *
 * It only ever REORDERS: a prepared pick that is not in the pool (adopted,
 * completed, or from another day) matches nothing and the old rules run
 * unchanged, so the card can never be left empty by a stale plan.
 */
export function selectCandidates(
  pool: CandidateTask[], currentHour: number, preparedTaskId?: string | null,
): Array<{ task: CandidateTask; why: WhyChip }> {
  const rules: Array<(t: CandidateTask) => boolean> = [
    t => !!preparedTaskId && t.task_id === String(preparedTaskId),
    t => t.status === 'in_progress',
    t => t.reasoning_tags.includes('user_pinned') || !!t.user_pinned_for_today,
    t => t.reasoning_tags.includes('deadline_soon') || t.deadline_type === 'hard',
    t => {
      const h = parseSlotHour(t.scheduled_time_slot)
      return h !== null && Math.abs(h - currentHour) <= 2
    },
    t => t.reasoning_tags.includes('smallest_first'),
    () => true,
  ]
  // Index of the "scheduled about now" rule above. Named because it is the
  // one rule whose matches need their OWN order: the rest inherit pool order,
  // but three blocks all within two hours of now must lead with the earliest,
  // not with whichever the plan happened to list first (live, 2026-09-11: the
  // hero was the 15:45 task, "then 14:15" beneath it, at 14:08).
  const ABOUT_NOW = 4
  const seen = new Set<string>()
  const out: Array<{ task: CandidateTask; why: WhyChip }> = []
  rules.forEach((rule, idx) => {
    let matched = pool.filter(t => !seen.has(t.task_id) && rule(t))
    if (idx === ABOUT_NOW) {
      matched = orderByStart(matched, t => t.scheduled_time_slot)
    }
    if (idx === rules.length - 1) {
      matched.sort((a, b) => (a.estimated_duration ?? 9999) - (b.estimated_duration ?? 9999))
    }
    for (const t of matched) {
      seen.add(t.task_id)
      out.push({ task: t, why: WHY_BY_RULE[idx] })
    }
  })
  return out
}

/**
 * "Not this one →" rotation: next index, except the FIRST tap offers the
 * smallest_first / shortest alternative if one exists (Quick Spark graft).
 * Wraps; never returns the current index unless it is the only candidate.
 */
export function nextCandidateIndex(
  ordered: Array<{ task: CandidateTask; why: WhyChip }>,
  currentIndex: number,
  rotationCount: number,
): number {
  if (ordered.length <= 1) return currentIndex
  if (rotationCount === 0) {
    const sparkIdx = ordered.findIndex((c, i) =>
      i !== currentIndex && (c.task.reasoning_tags.includes('smallest_first') || c.why === 'A quick one'))
    if (sparkIdx >= 0) return sparkIdx
  }
  return (currentIndex + 1) % ordered.length
}

// ── Free-time math (capacity chip) ──────────────────────────────────────────

export interface TimedEventLike {
  start_time?: string
  end_time?: string
  is_all_day?: boolean
  completed?: boolean
}

/**
 * Free minutes between max(now, 08:00) and 21:00 after subtracting timed
 * events (overlaps merged). Returns null when NO event parses (the chip hides
 * rather than fake a number — partial Phase-7 adoption stated, not faked).
 * Clamped to [0, 13h].
 */
export function computeFreeMinutes(events: TimedEventLike[], nowMinutes: number): number | null {
  const DAY_START = 8 * 60
  const DAY_END = 21 * 60
  const busy: Array<[number, number]> = []
  for (const ev of events) {
    // A COMPLETED block no longer occupies the user's future — the backend
    // deliberately excludes it from total_scheduled_minutes for the same
    // reason (orchestrator_service: "must NOT count toward the day's
    // scheduling math"). It still renders on the timeline, struck through.
    if (ev.is_all_day || ev.completed) continue
    const s = parseHHMM(ev.start_time)
    const e = parseHHMM(ev.end_time)
    if (s === null || e === null || e <= s) continue
    busy.push([s, e])
  }
  if (busy.length === 0) return null
  const lo = Math.max(nowMinutes, DAY_START)
  const hi = DAY_END
  if (lo >= hi) return 0
  busy.sort((a, b) => a[0] - b[0])
  let free = 0
  let cursor = lo
  for (const [s, e] of busy) {
    // Clamp the busy block to the window on BOTH sides; a block entirely
    // outside (e.g. starting after 21:00) clamps to empty and is skipped —
    // otherwise its start would inflate free time past the window edge.
    const cs = Math.min(Math.max(s, lo), hi)
    const ce = Math.min(Math.max(e, lo), hi)
    if (ce <= cs || ce <= cursor) continue
    if (cs > cursor) free += cs - cursor
    cursor = Math.max(cursor, ce)
  }
  if (cursor < hi) free += hi - cursor
  return Math.max(0, Math.min(free, 13 * 60))
}

// ── Now divider (timeline rail) ─────────────────────────────────────────────

/**
 * Index in `events` BEFORE which the "now" divider renders: the first timed
 * event starting after now, provided at least one earlier timed event exists.
 * Returns null when there is no sensible position (no timed events, all
 * before, or all after now).
 */
export function findNowDividerIndex(
  events: Array<{ start_time?: string; is_all_day?: boolean }>, nowMinutes: number,
): number | null {
  let sawEarlier = false
  for (let i = 0; i < events.length; i++) {
    const t = events[i].is_all_day ? null : parseHHMM(events[i].start_time)
    if (t === null) continue
    if (t <= nowMinutes) { sawEarlier = true; continue }
    return sawEarlier ? i : null
  }
  return null
}

// ── Core-complete derivation (line-2250-safe) ───────────────────────────────

/**
 * The celebration trigger. CRITICAL invariants:
 *  - TodayFocus.tsx filters completed tasks out of focusTasks before passing
 *    them down, so completion derives from progress.completed — never task
 *    statuses.
 *  - progress.total is a BUDGET (dailyFocusLimit fallback), non-zero even on a
 *    habits-only day, so it can NOT be the "day has tasks" signal. Task-day
 *    evidence is: any remaining focus task OR any completed task item
 *    (completed_tasks from the API).
 *  - A day with zero plan (no tasks, no habits) NEVER celebrates. Habits-only
 *    days celebrate when every habit is done.
 */
export function deriveCoreComplete(args: {
  focusCount: number
  completedTaskCount: number
  progressCompleted: number
  effectiveLimit: number
  remainingEffectiveFocus: number
  totalHabits: number
  completedHabits: number
}): boolean {
  const { focusCount, completedTaskCount, progressCompleted, effectiveLimit,
    remainingEffectiveFocus, totalHabits, completedHabits } = args
  const taskDay = focusCount > 0 || completedTaskCount > 0
  if (taskDay) {
    return effectiveLimit > 0 && progressCompleted >= effectiveLimit && remainingEffectiveFocus === 0
  }
  return totalHabits > 0 && completedHabits >= totalHabits
}

// ── Static copy (doctrine-guarded) ──────────────────────────────────────────

/** What each why-chip actually MEANS, in plain words.
 *
 * Owner ask 2026-08-30: "I like the previous `Why this: Next in line` option
 * and we could include the reasoning there." The chip was never the problem —
 * it names the rule that matched, which is the right amount for a chip. What
 * was missing is that the name alone does not tell you what the rule DID.
 * "Next in line" in particular reads as a queue position when it actually
 * means every stronger reason missed. So the label stays short and the
 * reasoning lives one tap behind it, at the point of the decision.
 */
export const WHY_DETAIL: Record<WhyChip | 'A rhythm for now', string> = {
  'Prepared for today': `${BRAND.assistant} put today's plan together before you opened it, and this is what it leads with. It used to be offered separately, which meant two answers to one question.`,
  'Already started': 'You had already started this one. Finishing something in motion beats opening something new.',
  'Pinned by you': `You pinned this to today yourself, so it outranks anything ${BRAND.assistant} would have picked.`,
  'Due soon': 'Its deadline is the nearest of anything on your list right now.',
  'Scheduled about now': 'You put this in your day for roughly this time.',
  'A quick one': 'It is the shortest thing on your list — the easiest way back into motion.',
  'Next in line': 'Nothing stood out as more urgent today, so this is simply the shortest of what is left. Not a verdict on how much it matters.',
  'Next on your schedule': 'It is the next thing on today\u2019s schedule, so it leads whatever else is waiting. Started, pinned and due-soon work would still come first.',
  'A rhythm for now': 'A habit you keep, offered because there is no task competing for this moment.',
}

/** Why a task is on your day, in a sentence.
 *
 * Owner ask 2026-08-30: "I cannot see why each of the tasks were selected for
 * today." The reasoning was always there — the orchestrator sends
 * focus_reasoning per task and the page rendered it as a two-word badge
 * ("Quick Win", "Due Soon"). A badge names the rule; it does not tell you what
 * the rule did. These do, and they are shown under the task itself rather than
 * behind a separate page.
 *
 * Keys are the orchestrator's reasoning tags (orchestrator_service:3921).
 */
export const REASON_DETAIL: Record<string, string> = {
  user_pinned: 'You pinned this to today yourself.',
  scheduled_today: 'You put this in your day for today.',
  carryover_incremental: 'You started this earlier and it is still open, so it carried over.',
  deadline_soon: 'Its deadline is close enough to matter today.',
  high_priority: 'You marked this one high priority.',
  smallest_first: 'It is the shortest thing on your list — the easiest way back into motion.',
  in_progress: 'You already have this one going.',
}

/** Join the tags a task carries into one honest sentence. */
export function reasonSentence(tags: string[] | undefined): string | null {
  const parts = (tags || []).map((t) => REASON_DETAIL[t]).filter(Boolean)
  if (parts.length === 0) return null
  return parts.join(' ')
}

export const COPY = {
  veilMorning: (mins: number) => `One thing is ready — about ${mins} min, when you are.`,
  veilEvening: 'One gentle thing left — when you are.',
  showMe: 'Show me',
  notThisOne: 'Not this one →',
  whyThis: 'Why this?',
  nightClosed: "Today's chapter is closed. Rest is part of the plan.",
  planTomorrow: 'Plan tomorrow',
  showMeAnyway: 'Show me anyway',
  focusOnThis: 'Start this',
  // ── North Star (2026-09-11) ─────────────────────────────────────────────
  /** Under the job: the first two rows whose clocks collide. Surfaced, never
   *  silently drawn (rule 5). */
  overlapLine: (a: string, b: string, at: string) => `${a} and ${b} overlap at ${at}.`,
  fixInTimeline: 'Sort it out in the timeline',
  /** The hero's steps count, in words ("1 of 3 steps"), not "1/3". */
  stepsOf: (done: number, total: number) => `${done} of ${total} steps`,
  /** Inside the "Why this?" disclosure — priority is context, not a badge. */
  priorityLine: (label: string) => `Priority: ${label}.`,
  /** The ⓘ that reveals a zone's rule line (rule 8). */
  zoneInfo: (name: string) => `What ${name} is for`,
  /** The Focus-mode switch on a finished day — present, off, and honest. */
  focusModeEmpty: 'Nothing left to focus on today.',
  feelingStuck: 'Feeling stuck?',
  tinyInstead: (mins: number) => `Something tiny instead — about ${mins} min?`,
  talkItThrough: 'Talk it through',
  gentleReset: 'Gentle reset',
  toBacklog: 'Backlog',
  blocked: 'Blocked',
  afterThat: 'The rest of today',

  // ── Zone vocabulary (owner ask 2026-08-30) ────────────────────────────
  // The page had an ORDER but never a PATH: every surface was individually
  // justified, individually capped and individually tone-guarded, and
  // nothing anywhere said what to do first. Five zones, fixed order, each
  // with a plain name and a one-sentence entry rule — and the spine says
  // out loud that only the first two need you.
  //
  // Zone 1 is named 'On a clock', not 'Can't miss'. 'Can't miss' is the
  // clearer phrase and it passes the worth-language guard, but this page's
  // own thesis (TodayOneClearThing.tsx:1) is that its first job is
  // physiological — lower the heart rate before asking for anything — and a
  // pressure phrase at the very top aims squarely at that reader. The rule
  // line carries the legibility instead, so the name can stay calm.
  // Names the sections as they now are: anything on a clock lives INSIDE Do,
  // so the spine no longer points at two places or at "Start here", which
  // stopped existing when the sections were renamed for their jobs.
  spine: 'One job under Do, and today went well. Everything below it is optional.',
  // "Hold me to it" is what every item page calls this exact thing (goals,
  // skills, habits). /today called it "On a clock", so one concept had two
  // names and neither said whose promise it was. Owner, 2026-09-08:
  // *"this is a section for hold me"*.
  zoneOnAClock: 'Hold me to it',
  zoneOnAClockRule: 'Deadlines and reminders you set yourself.',
  zoneOnAClockEmpty: 'Nothing you have put on a clock today.',
  // Four sections, each named for what the READER gets (owner, 2026-09-10,
  // after picking the four-section design): Do · Done · Stretch · Needs you.
  // The page had five prospective headings and three of them described work
  // today, so nothing said what was expected of you.
  //
  // ALL FOUR ALWAYS RENDER (owner, 2026-09-12: "Why is the UI bad again? …
  // Right now I am not seeing any stretch"). Stretch was gated on having rows,
  // so on a day with nothing to offer the card vanished and the page changed
  // shape — North Star rule 1. An empty section says in one line why it is
  // empty; it never disappears.
  zoneStartHere: 'Do',
  zoneStartHereRule: 'One job now, and what follows when it\u2019s done.',
  /** The connector on each queued row: "then · 10:00 · Review usage". */
  thenAt: 'then',
  zoneRestRule: 'Your other tasks and habits, today\u2019s schedule, and anything you left off.',
  zoneAsks: 'Needs you',
  // Says what the section is FOR, not merely who is speaking. Owner,
  // 2026-09-08: *"this is a section that asks me if there is some data missing
  // for scheduling/automom"* — the old rule named the speaker and left the
  // reader to work out the purpose.
  zoneAsksRule: `What ${BRAND.assistant} needs before it can plan or nudge. Ignore them and nothing breaks.`,
  zoneAsksEmpty: 'Nothing needs you right now.',
  zoneSoFar: 'Done',
  doneOf: (done: number, total: number) => `${done} of ${total}`,
  moodQuestion: 'How did today feel?',
  /** Shown until the answers actually say something; the server replaces it. */
  moodConsequence: 'Sets the day I suggest tomorrow.',
  /** The masthead pill once answered: "Felt · Great". */
  feltPrefix: 'Felt',
  /** Under the Done list when it names fewer than were finished. */
  moreDone: (n: number) => (n === 1 ? 'and 1 more' : `and ${n} more`),
  nothingFinishedYet: 'Nothing finished yet \u2014 the day is still open.',
  stillToCome: (n: number) =>
    n === 1 ? '1 still to come, under Do.' : `${n} still to come, under Do.`,
  zoneSoFarRule: 'What you finished, and how the week has gone.',
  /** The Do section's footer: promises you made, folded in rather than
   *  standing as their own heading — they are actions too. */
  onAClockNone: 'Nothing on a clock you set yourself.',
  /** Stretch: the optional slots the capacity ladder already grants. */
  zoneStretch: 'Stretch',
  /** Before the server has answered (the ladder's `optional_units` arrive
   *  with the Stretch payload). */
  stretchRuleShort: 'Beyond today\u2019s core \u2014 only if the rest is going well.',
  /** Names the rung in force, so the slot count is explained rather than
   *  asserted, and says what fills a slot (rule 8: the how is one tap away). */
  stretchRule: (mode: string, core: number, optional: number) =>
    `${mode.charAt(0).toUpperCase()}${mode.slice(1)} gives you ${core} core `
    + `and ${optional} optional. Offered here: the next step in each goal or skill, `
    + `and habits still open today. Take one on only if the rest is going well.`,
  /** The card with nothing to offer. Reassuring, not a report: everything the
   *  pool could draw from is already in the plan, finished, or has no path. */
  stretchEmpty: 'Nothing extra to take on right now.',
  /** Recovery grants no optional slot; the card says so instead of vanishing. */
  stretchNoSlots: (mode: string) =>
    `${mode.charAt(0).toUpperCase()}${mode.slice(1)} keeps today to the core \u2014 no extra slot.`,
  stretchSlots: (n: number) => n === 1 ? '1 slot' : `${n} slots`,
  stretchOpen: 'Open',
  /** The verb does what it says: the item joins today. */
  stretchTakeOn: 'Take on',
  addReminder: 'Add a reminder',
  // The two bare fractions ('1/2' beside a 12px target icon, '0/1' beside a
  // 12px loop icon) were the least decodable thing on the page.
  tasksDoneOf: (done: number, total: number) => `${done} of ${total} tasks done`,
  habitsDoneOf: (done: number, total: number) => `${done} of ${total} habits done`,
  // ── The rest-of-today execution timeline (constitution §6 Phase 2) ─────
  // Replaced the proximity ledger's own vocabulary ('Around Now', 'Timeline'):
  // the zone is now one time-ordered list, so there are no piles left to name.
  // Ratified decision #2 fixes the two words the user is allowed to see for
  // the calendar-commitment dimension. "Pinned" and "Locked" are rejected;
  // "HOLD"/"CONFIRMED" stay backend vocabulary (SC-36).
  flexible: 'Flexible',
  confirmed: 'Confirmed',
  flexibleMeaning: `${BRAND.assistant} picked this time. Move it, keep it, or let it go — nothing breaks.`,
  confirmedMeaning: 'You put this one here yourself.',
  unscheduledMeaning: 'No time set aside for this one — pick it up whenever it suits.',
  planCore: 'Core',
  planOptional: 'Optional',
  planHabit: 'Habit',
  rowInMotion: 'in motion',
  earlierToday: (n: number) => `Earlier today (${n})`,
  anytimeToday: 'Anytime today',
  windsDown: (time: string) => `Your day winds down around ${time}.`,
  // SC-21: an empty rest-of-day is the outcome, never a gap to fill. Nothing
  // here invites the user to put something in it.
  restOfDayClear: 'The rest of today is clear. That is a good way for a day to go.',
  verbConfirm: 'Confirm',
  verbKeepFlexible: 'Keep flexible',
  verbMove: 'Move',
  verbNotToday: 'Not today',
  verbSkipToday: 'Skip today',
  verbWhy: 'Why this?',
  verbStart: 'Start',
  verbDone: 'Done',
  verbConfirmed: 'Held for you.',
  verbKeptFlexible: 'Left as it is.',
  verbSkipped: 'Off today. Nothing else changed.',
  verbMoved: 'Moved.',
  // A 202 with applied:false is accepted-but-not-applied. Never say "done".
  verbPending: 'Sent — it has not taken effect yet.',
  verbConflict: 'This one moved while we were working on it. Refresh and try again.',
  verbFailed: 'That did not go through.',
  moveTo: 'Move to',
  moveKeepFlexible: 'Keep it flexible',
  moveConfirmLabel: 'Move it',
  moveSaving: 'Moving…',
  moveCancel: 'Cancel',
  whyLoading: 'Looking…',
  whyUnavailable: `${BRAND.assistant} cannot explain this one right now.`,
  whySourceUserDirect: 'You chose this time yourself.',
  whySourceUserConfirmation: `${BRAND.assistant} suggested this time and you kept it.`,
  whySourceAiProposal: `${BRAND.assistant} suggested this time. It is yours to move.`,
  whySourceRule: 'This comes from a rhythm you set.',
  whySourceDerived: `This one predates the record, so ${BRAND.assistant} worked it out from the block itself.`,
  whyPartOf: (title: string) => `Part of ${title}.`,
  whyDue: (date: string) => `Due ${date}.`,

  restingForToday: (n: number) => `Left off today (${n})`,
  restingCaption: 'These wait quietly — nothing is lost.',
  restingHint: (now: number, resting: number) => `${now} now · ${resting} resting`,
  // Defect fix 2026-08-29: the old line ('Habits are on track…') claimed a
  // habit-completion check its gate never makes — the gate only means "you
  // have habits and no focus tasks", so it fired at 0-of-1 done. This
  // sentence is true in every state the gate admits.
  habitsOnTrack: 'Nothing else is scheduled today. Want to take on more?',
  arrivingMorning: 'How are you arriving today?',
  arrivingEvening: 'How did today feel?',
  checkinFootnote: "Just for you — this never changes today's plan.",
  // Defect fix 2026-08-29: renamed from 'With You' — that name is taken by
  // the chat composer's With You companion chips. This rail is Auto-Mom's
  // check-ins (pause revisits, adjustments, goal reviews, checkpoints).
  withYou: 'Needs you',
  wins: 'Wins',
  winsToday: (n: number) => `${n} today`,
  yesterdayGlow: (n: number) => `${n} from yesterday still glowing`,
  yesterday: 'Yesterday',
  moreWins: (n: number) => `+${n} more`,
  howWeighed: 'How today was weighed',
  seeEverything: 'See every task and why →',
  weighedSentence: (total: number, goal: string | undefined, completed: number, inProgress: number) =>
    `Today held ${total} planned ${total === 1 ? 'thing' : 'things'}${goal ? ` — ${goal} was the aim` : ''}. ` +
    `${completed} ${completed === 1 ? 'is' : 'are'} done and ${inProgress} in motion.`,
  setDown: (n: number) => `${n} ${n === 1 ? 'thing' : 'things'} consciously set down.`,
  backlogSetDown: (n: number) => `${n} set down`,
  aboveAndBeyond: "A bonus beyond today's plan — never the new bar.",
  resting: 'Resting — see you tomorrow.',
  stopForToday: 'Stop for today',
  seeOneOptional: 'See one optional thing',
  deadlinesAttention: (n: number) => `${n} deadline${n > 1 ? 's' : ''} want${n > 1 ? '' : 's'} attention today`,
  deadlinesRule: 'Hard dates you cannot move.',
  workOnThisNow: 'Open this',
  risk: 'Risk:',
  now: 'now',
  inProgressTitle: 'In Progress',
  inProgressCaption: "Active but not in today's focus. Pin to add.",
  backlogLabel: 'Backlog:',
  pendingCount: (n: number) => `${n} pending`,
  wideOpenTitle: 'Your day is wide open',
  wideOpenBody: 'No tasks or habits scheduled. Start by setting a goal or scheduling existing tasks.',
  wideOpenBodyAfterWins: 'Nice work yesterday — today is a fresh page. Set a goal or schedule something when you are ready.',
  setAGoal: 'Set a Goal',
  createATask: 'Create a Task',
  schedule: 'Schedule',
  scheduleTasks: 'Schedule Tasks',
  newTask: 'New Task',
  newGoal: 'New Goal',
  coreUnderway: 'planned today',
  loadingSteps: 'Loading steps...',
  aRhythmForNow: 'A rhythm for now',
  freeChip: (label: string) => `≈ ${label}`,
  why: 'why?',
  complete: 'Complete',
  checkinWaiting: 'Daily check-in waiting',
  withYouCount: (n: number) => `${n} to answer`,
  updatesCount: (n: number) => `${n} to answer`,
  // Defect fix 2026-08-29: the masthead used to say a bare "N of M done" where
  // M is the FOCUS budget — unreadable on a habits-only day. Name the unit.
  focusDone: (done: number, total: number) =>
    `${done} of ${total} focus tasks done — one clear move at a time.`,

  // ── The phone layout (owner's mockup, 2026-09-12) ────────────────────────
  // Header band: the date, one pill saying what is left and how much of the
  // day is open, Quick add. Under it the due bar, the Focus-mode switch, and
  // the cards in the mockup's order: Reminders (held to the day) · Focus ·
  // Up next with Stretch beneath it · Needs you · the week · the check-in.
  quickAdd: 'Quick add',
  quickAddPlaceholder: 'Add a task — a title is enough',
  quickAddSaved: 'Added to your list.',
  quickAddFailed: 'That did not save. Try again.',
  focusModeLabel: 'Focus mode',
  /** The pill is a disclosure: tap it and the day's finished list opens. */
  pillOpens: 'Show what you finished today',
  pillCloses: 'Hide what you finished today',
  zoneFocus: 'Focus',
  zoneFocusRule: 'One thing now. Start it, come back to it later, or move it to tomorrow.',
  /** "Later" shows the next pick; this one stays on today. */
  laterVerb: 'Later',
  laterTitle: 'Show the next one instead. This stays on today.',
  notTodayTitle: 'Moves it to tomorrow and frees today.',
  notTodayDone: 'Moved to tomorrow.',
  zoneUpNext: 'Up next',
  zoneUpNextRule: 'What follows the one thing, in the order it happens — then what you could take on beyond today’s core.',
  upNextEmpty: 'Nothing else queued after this.',
  stretchSub: 'Stretch · optional',
  zoneReminders: 'Reminders',
  zoneRemindersRule: 'Held for today with no clock. Done when you say so; give one a time and it moves to the bar at the top.',
  zoneRemindersEmpty: 'Nothing held for today.',
  weekTitle: 'This week',
  weekRule: 'Each of the last five days against the best of them — a rhythm, not a tally.',
  weekDay: (label: string, total: number) =>
    `${label}: ${total === 1 ? '1 thing finished' : `${total} things finished`}`,
  checkinTitle: 'Evening check-in',
  moodPicked: (label: string) => `Felt ${label.toLowerCase()} today.`,
} as const

/** Every static user-facing literal — the doctrine guard's input. Functions in
 * COPY are sampled with representative values so their templates are covered. */
export const ALL_STATIC_STRINGS: string[] = [
  COPY.veilMorning(15), COPY.veilEvening, COPY.showMe, COPY.notThisOne, COPY.whyThis,
  COPY.nightClosed, COPY.planTomorrow, COPY.showMeAnyway, COPY.focusOnThis,
  COPY.feelingStuck, COPY.tinyInstead(5), COPY.talkItThrough, COPY.gentleReset,
  COPY.toBacklog, COPY.blocked, COPY.afterThat,
  COPY.spine, COPY.zoneOnAClock, COPY.zoneOnAClockRule, COPY.zoneOnAClockEmpty,
  COPY.zoneStartHere, COPY.zoneStartHereRule, COPY.zoneRestRule,
  COPY.zoneAsks, COPY.zoneAsksRule, COPY.zoneSoFar, COPY.zoneSoFarRule,
  COPY.addReminder, COPY.tasksDoneOf(1, 2), COPY.habitsDoneOf(0, 1),
  COPY.moodQuestion, COPY.moodConsequence, COPY.feltPrefix, COPY.moreDone(2),
  COPY.overlapLine('A', 'B', '15:00'), COPY.fixInTimeline, COPY.stepsOf(1, 3), COPY.priorityLine('Nice to have'), COPY.zoneInfo('Do'),
  COPY.focusModeEmpty,
  COPY.restingForToday(2), COPY.restingCaption, COPY.restingHint(2, 3),
  COPY.flexible, COPY.confirmed, COPY.flexibleMeaning, COPY.confirmedMeaning,
  COPY.unscheduledMeaning, COPY.planCore, COPY.planOptional, COPY.planHabit,
  COPY.rowInMotion, COPY.earlierToday(2), COPY.anytimeToday,
  COPY.windsDown('22:30'), COPY.restOfDayClear,
  COPY.verbConfirm, COPY.verbKeepFlexible, COPY.verbMove, COPY.verbNotToday,
  COPY.verbSkipToday, COPY.verbWhy, COPY.verbStart, COPY.verbDone,
  COPY.verbConfirmed, COPY.verbKeptFlexible, COPY.verbSkipped, COPY.verbMoved,
  COPY.verbPending, COPY.verbConflict, COPY.verbFailed,
  COPY.moveTo, COPY.moveKeepFlexible, COPY.moveConfirmLabel, COPY.moveSaving,
  COPY.moveCancel, COPY.whyLoading, COPY.whyUnavailable,
  COPY.whySourceUserDirect, COPY.whySourceUserConfirmation,
  COPY.whySourceAiProposal, COPY.whySourceRule, COPY.whySourceDerived,
  COPY.whyPartOf('the quarterly report'), COPY.whyDue('2026-09-01'),
  COPY.habitsOnTrack, COPY.arrivingMorning, COPY.arrivingEvening, COPY.checkinFootnote,
  COPY.withYou, COPY.wins, COPY.winsToday(4), COPY.yesterdayGlow(3), COPY.yesterday,
  COPY.moreWins(2), COPY.howWeighed, COPY.seeEverything,
  COPY.weighedSentence(5, 'a gentle start', 2, 1),
  COPY.weighedSentence(1, undefined, 1, 0), COPY.setDown(1), COPY.setDown(3),
  COPY.backlogSetDown(1), COPY.aboveAndBeyond, COPY.resting, COPY.stopForToday,
  COPY.seeOneOptional, COPY.deadlinesAttention(1), COPY.deadlinesAttention(2),
  COPY.workOnThisNow, COPY.risk, COPY.now, COPY.inProgressTitle,
  COPY.inProgressCaption, COPY.backlogLabel, COPY.pendingCount(4),
  COPY.wideOpenTitle, COPY.wideOpenBody, COPY.wideOpenBodyAfterWins, COPY.setAGoal, COPY.createATask,
  COPY.schedule, COPY.scheduleTasks, COPY.newTask, COPY.newGoal, COPY.coreUnderway,
  COPY.loadingSteps, COPY.aRhythmForNow, COPY.why, COPY.complete,
  COPY.checkinWaiting, COPY.withYouCount(2), COPY.updatesCount(3),
  COPY.focusDone(1, 3), COPY.focusDone(0, 1),
  COPY.quickAdd, COPY.quickAddPlaceholder, COPY.quickAddSaved, COPY.quickAddFailed,
  COPY.focusModeLabel, COPY.pillOpens, COPY.pillCloses,
  COPY.zoneFocus, COPY.zoneFocusRule, COPY.laterVerb, COPY.laterTitle,
  COPY.notTodayTitle, COPY.notTodayDone,
  COPY.zoneUpNext, COPY.zoneUpNextRule, COPY.upNextEmpty, COPY.stretchSub,
  COPY.zoneReminders, COPY.zoneRemindersRule, COPY.zoneRemindersEmpty,
  COPY.weekTitle, COPY.weekRule, COPY.weekDay('T', 1), COPY.weekDay('W', 3),
  COPY.checkinTitle, COPY.moodPicked('Good'),
  'Pinned by you', 'Already started', 'Due soon', 'Scheduled about now',
  'A quick one', 'Next in line',
  // Literals rendered by the component outside COPY (kept here so the
  // doctrine guard genuinely covers EVERY user-facing string on the page).
  'Try Again', 'Hard Deadline', 'Hard deadline', 'TODAY', 'TOMORROW',
  '3d OVERDUE', 'Mark done', 'Unpin from today', 'Enter focus mode',
  'Refresh', 'Refreshing', 'Loading today', 'All day',
  'Other Habits', 'Habits', 'Other Tasks', 'Tasks', 'Pin', 'Log',
  'Must Do', 'Important', 'Normal', 'Nice to Have',
  'Carryover needs a decision', '3 tasks from before.', 'Review',
  'Today', '3 days', '7 days', '3D', '7D',
  'Time range filter', 'Daily check-in waiting', 'Collapse', 'Expand',
]
